import I18n from 'I18n';
import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Text } from 'foundations-components/components/Text';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { View } from 'foundations-components/components/View';
import { ActionsMenu } from 'foundations-components/components/ActionsMenu';
import SummaryNoSearchResults from 'documents-ui/components/summary/SummaryNoSearchResults';
import SummarySearchFailure from 'documents-ui/components/summary/SummarySearchFailure';
import SummaryRecipientsZeroState from 'documents-ui/components/summary/SummaryRecipientsZeroState';
import SummaryRecipientsTable from 'documents-ui/components/summary/recipientsTable/SummaryRecipientsTable';
import DetailPanel from 'documents-ui/components/summary/detail/DetailPanel';
import { trackInteraction } from 'documents-lib/js/tracking/tracker';
import { OPEN_VIEW_USAGE_DETAIL } from 'documents-lib/js/tracking/actions';
import { VISITORS_PAGE_LIMIT } from 'documents-ui/api/DocumentsApi';
import { isUngatedToCHIRPEndpoints } from 'documents-ui/utils/gates';
import { useGetDocumentVisitors } from '../hooks/useGetDocumentVisitors';
import { DetailPanelCHIRP } from '../components/summary/detail/DetailPanelCHIRP';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SummaryRecipientsContainer = ({
  allDocumentSlides,
  documentsRevisits,
  documentVisitorSearchQuery,
  gdprSettings,
  deckId,
  title,
  deckVisitors,
  hasMore,
  visitorsIsLoading: visitorsIsLoadingREST,
  visitorsFetchFailure: visitorsFetchFailureREST,
  fetchDocumentVisitors,
  fetchDocumentRevisits
}) => {
  const isUngatedToChirp = isUngatedToCHIRPEndpoints();
  const [selectedDeckVisitor, setSelectedDeckVisitor] = useState(null);
  const [selectedDocumentVisitor, setSelectedDocumentVisitor] = useState(null);
  const [pageLimit, setPageLimit] = useState(VISITORS_PAGE_LIMIT);
  const [searchQueryCHIRP, setSearchQueryCHIRP] = useState('');
  const getRevisits = () => {
    if (!selectedDeckVisitor) {
      return null;
    }
    return documentsRevisits.getIn([deckId, selectedDeckVisitor.get('vid')]);
  };
  const {
    data: documentVisitorsCHIRP,
    loading: documentVisitorsCHIRPLoading,
    error: documentVisitorsCHIRPError
  } = useGetDocumentVisitors({
    documentId: deckId,
    limit: pageLimit,
    offset: 0,
    searchQuery: searchQueryCHIRP
  });
  const handleOnViewDetails = selectedVisitor => {
    const vid = selectedVisitor.get('vid');
    if (!documentsRevisits.hasIn([deckId, vid])) {
      fetchDocumentRevisits({
        deckId,
        vid
      });
    }
    trackInteraction(OPEN_VIEW_USAGE_DETAIL);
    setSelectedDeckVisitor(selectedVisitor);
  };
  const handleOnViewDetailsCHIRP = selectedVisitor => {
    trackInteraction(OPEN_VIEW_USAGE_DETAIL);
    setSelectedDocumentVisitor(selectedVisitor);
  };
  const handleOnSearchChange = e => {
    const searchQuery = e.target.value;
    fetchDocumentVisitors({
      documentVisitorSearchQuery: searchQuery,
      deckId,
      initial: true,
      limit: pageLimit
    });
  };
  const handleOnSearchChangeCHIRP = e => {
    const searchQuery = e.target.value;
    setSearchQueryCHIRP(searchQuery);
  };
  const handlePerPageChange = perPage => {
    setPageLimit(perPage);
    fetchDocumentVisitors({
      initial: true,
      limit: perPage,
      deckId,
      documentVisitorSearchQuery
    });
    const summaryHeaderElement = document.getElementById('summary-header');
    if (summaryHeaderElement) {
      summaryHeaderElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleCloseDetailPanel = useCallback(() => {
    if (isUngatedToChirp) {
      setSelectedDocumentVisitor(null);
    } else {
      setSelectedDeckVisitor(null);
    }
  }, [isUngatedToChirp]);
  const renderTable = () => {
    const hasNoSearchResultsREST = documentVisitorSearchQuery !== '' && deckVisitors && deckVisitors.isEmpty();
    const hasNoSearchResultsCHIRP = documentVisitorSearchQuery !== '' && (documentVisitorsCHIRP === null || documentVisitorsCHIRP === void 0 ? void 0 : documentVisitorsCHIRP.totalCount) === 0;
    const hasNoSearchResults = isUngatedToChirp ? hasNoSearchResultsCHIRP : hasNoSearchResultsREST;
    const visitorsFetchFailureCHIRP = documentVisitorsCHIRPError;
    const visitorsFetchFailure = isUngatedToChirp ? visitorsFetchFailureCHIRP : visitorsFetchFailureREST;
    const visitorsIsLoadingCHIRP = documentVisitorsCHIRPLoading;
    const visitorsIsLoading = isUngatedToChirp ? visitorsIsLoadingCHIRP : visitorsIsLoadingREST;
    if (visitorsFetchFailure) {
      return /*#__PURE__*/_jsx(SummarySearchFailure, {});
    }
    if (!deckVisitors && visitorsIsLoading) {
      return /*#__PURE__*/_jsx(TFlex, {
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: 200,
          layout: "centered"
        })
      });
    }
    if (hasNoSearchResults) {
      return /*#__PURE__*/_jsx(SummaryNoSearchResults, {});
    }
    const showZeroState = isUngatedToChirp ? (documentVisitorsCHIRP === null || documentVisitorsCHIRP === void 0 ? void 0 : documentVisitorsCHIRP.totalCount) === 0 : deckVisitors.isEmpty();
    if (showZeroState) {
      return /*#__PURE__*/_jsx(SummaryRecipientsZeroState, {
        gdprSettings: gdprSettings,
        deckId: deckId,
        name: title
      });
    }
    return /*#__PURE__*/_jsx(SummaryRecipientsTable, {
      deckId: deckId,
      deckVisitors: deckVisitors,
      documentVisitorSearchQuery: documentVisitorSearchQuery,
      fetchDocumentVisitors: fetchDocumentVisitors,
      hasMore: hasMore,
      onViewDetails: handleOnViewDetails,
      onViewDetailsCHIRP: handleOnViewDetailsCHIRP,
      pageLimit: pageLimit,
      searchQueryCHIRP: searchQueryCHIRP
    });
  };
  const renderDetailPanel = () => {
    if (isUngatedToChirp && selectedDocumentVisitor) {
      return /*#__PURE__*/_jsx(DetailPanelCHIRP, {
        documentId: deckId,
        onClose: handleCloseDetailPanel,
        selectedVisitor: selectedDocumentVisitor
      });
    }
    if (!selectedDeckVisitor) {
      return null;
    }
    return /*#__PURE__*/_jsx(DetailPanel, {
      allDocumentSlides: allDocumentSlides,
      deckVisitor: selectedDeckVisitor,
      revisits: getRevisits(),
      onClose: handleCloseDetailPanel
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "summary-recipients-container",
    children: [/*#__PURE__*/_jsxs(TFlex, {
      justify: "between",
      align: "center",
      marginBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.recipientsTable.title"
        })
      }), /*#__PURE__*/_jsx(View, {
        inlineSize: "360px",
        children: /*#__PURE__*/_jsx(SearchInput, {
          icon: true,
          clearable: true,
          placeholder: I18n.text('summary.recipientsTable.search.placeholder'),
          value: isUngatedToChirp ? searchQueryCHIRP : documentVisitorSearchQuery,
          onChange: isUngatedToChirp ? handleOnSearchChangeCHIRP : handleOnSearchChange
        })
      })]
    }), /*#__PURE__*/_jsx(TFlex, {
      justify: "end",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(View, {
        marginInlineStart: "400",
        children: /*#__PURE__*/_jsx(ActionsMenu, {
          use: "transparent",
          referenceText: I18n.text('summary.recipientsTable.paginator.perPageDropdownButton', {
            count: pageLimit
          }),
          actions: [{
            text: I18n.text('summary.recipientsTable.paginator.tenPerPage'),
            value: 10
          }, {
            text: I18n.text('summary.recipientsTable.paginator.twentyFivePerPage'),
            value: 25
          }, {
            text: I18n.text('summary.recipientsTable.paginator.fiftyPerPage'),
            value: 50
          }],
          onActionClick: ({
            value
          }) => {
            handlePerPageChange(Number(value));
          }
        })
      })
    }), renderTable(), renderDetailPanel()]
  });
};
export default SummaryRecipientsContainer;